import styled from "@emotion/styled"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTimes,
  faCircle,
} from "@fortawesome/free-solid-svg-icons"
import {
  faChevronDown,
} from "@fortawesome/pro-light-svg-icons"
import get from "lodash/get"
import React, { useContext, useEffect, useRef, useState } from "react"
import ScrollLock from "react-scrolllock"
import tw from "twin.macro"
import { getFirstFocusable } from "../utils/helpers"
import linkTransformer from "../utils/linkTransformer"
import Container from "./Container"
import Link from "./Link"
import { PageContext } from "./PageProvider"
import ThemeContext from "../context/theme-context"

library.add(
  faChevronDown,
  faCircle,
  faTimes,
)


const StyledMenuWrapper = styled.div`
  ${tw`bg-blue fixed inset-0 h-full w-full z-30 flex items-center lg:hidden`}
  transition: opacity 300ms ease-in-out, transform 300ms ease-in-out, visibility 300ms ease-in-out;
  ${(props) =>
    props.isOpen && { opacity: 1, zIndex: 30, visibility: "visible" }}
  ${(props) =>
    !props.isOpen && {
      opacity: 0,
      visibility: "hidden",
      pointerEvents: "none",
      transform: "scale(1.1)",
    }}
`

const CloseButton = styled.button`
  ${tw`absolute top-0 right-0 mr-4 mt-4 text-white opacity-25`}
  transition: opacity 300ms ease-in-out;
  &:hover {
    ${tw`opacity-100`}
  }
`
const Navigation = styled.ul`
  ${tw`mx-auto max-w-full text-center`}
  width: 375px;
`

const StyledNavItem = styled.li`
  ${tw`border-white border-b uppercase text-white tracking-widest`}
  > a svg {
    transform: rotate(0);
    transition: transform 150ms ease-in-out;
  }
  &.is-open > a svg {
    transform: rotate(180deg);
  }
`
const NavLink = styled(Link)`
  ${tw`inline-block w-full p-2 my-1 rounded`}
  transition-property: color, background-color;
  transition-duration: 300ms;
  transition-timing-function: ease-in-out;
  &:hover {
    ${tw`bg-white text-black`}
  }
  &.active,
  &.active:hover {
    ${tw`bg-black text-white`}
  }
`

const NavButton = styled(NavLink)`
  ${tw`bg-white text-black`}
  &:hover {
    ${tw`bg-black text-white`}
  }
`
const SubNav = styled.ul`
  ${tw`hidden bg-white`}
  ${StyledNavItem}.is-open & {
    ${tw`block`}
  }
`
const SubNavItem = styled.li`
  ${tw`block p-1`}
  & + & {
    ${tw`border-t border-gray`}
  }
`
const SubNavLink = styled(Link)`
  ${tw`text-gray text-sm block py-1`}
  transition-property: color, background-color;
  transition-duration: 300ms;
  transition-timing-function: ease-in-out;
  &:hover {
    ${tw`bg-gray-light`}
  }
  &.active,
  &.active:hover {
    ${tw`bg-gray-light text-blue`}
  }
`
const SubNavButton = styled(SubNavLink)`
  ${tw`border border-blue rounded text-blue`}
  &.active,
  &.active:hover {
    ${tw`bg-blue text-white`}
  }
`

const MobileMenu = ({ global }) => {
  const { toggleMenu, isMenuOpen } = useContext(PageContext)
  const { hydrated: isClient } = useContext(ThemeContext)
  const ref = useRef()
  const primaryNavigationLinks = get(
    global,
    "elements.primary_navigation_links.value"
  )

  useEffect(() => {
    const keydownHandler = (e) => {
      if (isMenuOpen && (e.key === "Escape" || e.keyCode === 27)) {
        toggleMenu()
      }
    }

    document.addEventListener("keydown", keydownHandler)

    if (ref.current) {
      const firstFocusable = getFirstFocusable(
        ref.current.querySelector("#mobile-menu")
      )
      setTimeout(() => {
        firstFocusable && firstFocusable.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener("keydown", keydownHandler)
    }
  }, [toggleMenu, isMenuOpen, ref])

  return (
    <>
      <StyledMenuWrapper isOpen={isMenuOpen} ref={ref}>
        <CloseButton aria-label="Close" onClick={toggleMenu}>
          <FontAwesomeIcon
            icon={["fas", "times"]}
            mask={isClient ? ["fas", "circle"] : null}
            size="2x"
            transform="shrink-6"
          />
        </CloseButton>
        <Container>
          <Navigation id="mobile-menu" aria-labelledby="mobile-menu-button">
            {primaryNavigationLinks.map((navItem, index) => {
              const id = get(navItem, "system.id")
              const partiallyActive = index > 0 ? true : false

              return (
                <NavItem
                  item={navItem}
                  key={id}
                  partiallyActive={partiallyActive}
                />
              )
            })}
          </Navigation>
        </Container>
      </StyledMenuWrapper>
      <ScrollLock isActive={isMenuOpen} />
    </>
  )
}

const NavItem = ({ item, partiallyActive }) => {
  const transformedLink = linkTransformer(item)
  const style = get(item, "elements.style.value[0].codename") || "link"
  const children = get(item, "elements.children.value") || []
  const hasChildren = children.length ? true : false
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = (event) => {
    if (hasChildren) {
      event.preventDefault()
      setIsOpen(!isOpen)
    }
  }

  return (
    <StyledNavItem isOpen={isOpen} className={isOpen ? `is-open` : ``}>
      {
        {
          link: (
            <NavLink
              to={transformedLink.to}
              activeClassName="active"
              partiallyActive={partiallyActive}
              onClick={handleClick}
            >
              {transformedLink.label}
              {hasChildren ? (
                <>
                  {` `}
                  <FontAwesomeIcon icon={["fal", "chevron-down"]} />
                </>
              ) : null}
            </NavLink>
          ),
          button: (
            <NavButton
              to={transformedLink.to}
              activeClassName="active"
              partiallyActive={partiallyActive}
              onClick={handleClick}
            >
              {transformedLink.label}
              {hasChildren ? (
                <>
                  {` `}
                  <FontAwesomeIcon icon={["fal", "chevron-down"]} />
                </>
              ) : null}
            </NavButton>
          ),
        }[style]
      }
      {children.length ? (
        <SubNav>
          {children.map((child, index) => {
            const transformedChildLink = linkTransformer(child)
            const childId = get(child, "system.id")
            const childStyle =
              get(child, "elements.style.value[0].codename") || "link"
            return (
              <SubNavItem key={childId}>
                {
                  {
                    link: (
                      <SubNavLink
                        to={transformedChildLink.to}
                        activeClassName="active"
                      >
                        {transformedChildLink.label}
                      </SubNavLink>
                    ),
                    button: (
                      <SubNavButton
                        to={transformedChildLink.to}
                        activeClassName="active"
                      >
                        {transformedChildLink.label}
                      </SubNavButton>
                    ),
                  }[childStyle]
                }
              </SubNavItem>
            )
          })}
        </SubNav>
      ) : null}
    </StyledNavItem>
  )
}

export default MobileMenu
